import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t, language } = useLanguage();

  const footerLinks = {
    solutions: [
      { name: t('freeAIAssessmentFooter'), href: '/quickscan/start' },
      { name: t('freeUseCases'), href: '/use-cases' },
      { name: language === 'nl' ? 'AI Workshops' : 'AI Workshops', href: '/workshop' },
      { name: t('consulting'), href: '/contact' },
    ],
    company: [
      { name: t('aboutUs'), href: '/about' },
      { name: t('team'), href: '/team' },
      { name: t('careers'), href: '/careers' },
      { name: t('contact'), href: '/contact' },
    ],
    resources: [
      { name: t('blog'), href: '/blog' },
      { name: t('faq'), href: '/faq' },
    ],
  };

  return (
    <footer className="bg-slate-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand & Team */}
          <div className="lg:col-span-2">
            {/* Logo is prominent in the header — footer stays text-only for a lighter footprint */}
            <div className="mb-4">
              <h3 className="text-white text-lg font-bold">Humanistix</h3>
              <p className="text-xs text-blue-300 font-medium">AI for HR</p>
            </div>
            {/* Tagline */}
            <p className="text-blue-400 font-semibold text-lg mb-6">
              {language === 'nl' ? 'Praktische AI voor HR. Meetbare ROI.' : 'Practical AI for HR. Measurable ROI.'}
            </p>
            
            {/* Team Members with Photos */}
            <div className="space-y-4">
              {/* Dominique */}
              <div className="flex items-start gap-3">
                <img 
                  src="/dominique-photo.png" 
                  alt="Dominique Dejonghe" 
                  className="w-12 h-12 rounded-full object-cover bg-gray-700"
                />
                <div>
                  <p className="text-sm font-medium text-white">Dominique Dejonghe</p>
                  <p className="text-xs text-blue-400 mb-1">AI for HR change and transformation agent</p>
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Phone className="h-3 w-3 text-blue-500" />
                      <span>+32 498 900 947</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Mail className="h-3 w-3 text-blue-500" />
                      <span>dominique@humanistix.be</span>
                    </div>
                    <a 
                      href="https://www.linkedin.com/in/ddejonghe" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-gray-400 hover:text-blue-500 transition-colors"
                    >
                      <Linkedin className="h-3 w-3 text-blue-500" />
                      <span>LinkedIn</span>
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Luc */}
              <div className="flex items-start gap-3">
                <img 
                  src="/luc-photo.png" 
                  alt="Luc Langeraert" 
                  className="w-12 h-12 rounded-full object-cover bg-gray-700"
                />
                <div>
                  <p className="text-sm font-medium text-white">Luc Langeraert</p>
                  <p className="text-xs text-blue-400 mb-1">Digital HR & Change</p>
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Phone className="h-3 w-3 text-blue-500" />
                      <span>+32 494 56 18 19</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Mail className="h-3 w-3 text-blue-500" />
                      <span>luc@humanistix.be</span>
                    </div>
                    <a 
                      href="https://www.linkedin.com/in/luclangeraert" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-gray-400 hover:text-blue-500 transition-colors"
                    >
                      <Linkedin className="h-3 w-3 text-blue-500" />
                      <span>LinkedIn</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Location */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <MapPin className="h-4 w-4 text-blue-500" />
                <span>Antwerpen - Gent - Brussel</span>
              </div>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('solutions')}</h3>
            <ul className="space-y-2">
              {footerLinks.solutions.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm hover:text-blue-500 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('company')}</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm hover:text-blue-500 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('resources')}</h3>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm hover:text-blue-500 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © {currentYear} Humanistix. {t('allRightsReserved')}
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="https://www.linkedin.com/company/humanistix" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-blue-500 transition-colors"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
            <div className="flex gap-4 text-sm text-gray-500">
              <Link to="/privacy" className="hover:text-blue-500 transition-colors">{t('privacyPolicy')}</Link>
              <Link to="/terms" className="hover:text-blue-500 transition-colors">{t('termsOfService')}</Link>
              <Link to="/cookies" className="hover:text-blue-500 transition-colors">{t('cookiePolicy')}</Link>
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-gray-500" data-testid="footer-made-with-love">
            this site was built with 💙 in 🇧🇪 augmented with 🤖 ai
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
